<template>
  <Teleport to="body">
    <div class="vote-config-backdrop" @click.self="emit('close')">
      <div class="vote-config-panel">
        <div class="panel-header">
          <h3 class="panel-title" @click="onTitleClick" :title="`Clicks: ${headlineClickCount}/5`">Configure Vote Services</h3>
          <button class="close-btn" @click="emit('close')" aria-label="Close" title="Close"><X :size="16" /></button>
        </div>

        <div class="panel-body">
          <VoteServiceEditor :show-manual-form-btn="showManualFormBtn" />

          <div class="refresh-section">
            <button class="refresh-btn" @click="refreshFromAPI" :disabled="store.isLoadingAvailableServices">
              <RefreshCw :size="14" :class="{ spinning: store.isLoadingAvailableServices }" />
              Refresh from API
            </button>
            <p v-if="refreshError" class="refresh-error">{{ refreshError }}</p>
            <p v-if="refreshSuccess" class="refresh-success">Services updated</p>
          </div>
        </div>
      </div>
    </div>
  </Teleport>
</template>

<script setup lang="ts">
import { ref, computed } from "vue";
import { X, RefreshCw } from "lucide-vue-next";
import { useApiStore } from "@/stores/api";
import VoteServiceEditor from "@/components/rules/VoteServiceEditor.vue";

const emit = defineEmits<{
  (e: "close"): void;
}>();

const store = useApiStore();
const refreshError = ref<string | null>(null);
const refreshSuccess = ref(false);
const headlineClickCount = ref(0);

const showManualFormBtn = computed(
  () => headlineClickCount.value >= 5 || store.availableServices === null
);

function onTitleClick() {
  if (headlineClickCount.value < 5) {
    headlineClickCount.value++;
  }
}

async function refreshFromAPI() {
  refreshError.value = null;
  refreshSuccess.value = false;

  try {
    await store.refreshAvailableServices();
    refreshSuccess.value = true;
    // Clear success message after 2 seconds
    setTimeout(() => {
      refreshSuccess.value = false;
    }, 2000);
  } catch (e) {
    refreshError.value = String(e);
  }
}
</script>

<style scoped lang="scss">
.vote-config-backdrop {
  position: fixed;
  inset: 0;
  background: var(--bg-overlay);
  z-index: 2010;
  display: flex;
  align-items: center;
  justify-content: center;
}

.vote-config-panel {
  background: var(--bg-surface);
  color: var(--text-primary);
  border: 1px solid var(--border);
  border-radius: var(--radius-lg);
  width: min(480px, 95vw);
  max-height: 90vh;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.panel-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 16px;
  background: var(--bg-elevated);
  border-bottom: 1px solid var(--border);

  h3 {
    margin: 0;
    font-size: 1rem;
  }

  .panel-title {
    cursor: pointer;
    user-select: none;

    &:active {
      opacity: 0.8;
    }
  }

  .close-btn {
    background: none;
    border: none;
    color: var(--text-primary);
    cursor: pointer;
    padding: 0;
    display: flex;
    align-items: center;
    &:hover { color: var(--accent); }
  }
}

.panel-body {
  overflow-y: auto;
  padding: 16px;
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.refresh-section {
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding-top: 12px;
  border-top: 1px solid var(--border);
}

.refresh-btn {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 7px 12px;
  background: var(--bg-elevated);
  border: 1px solid var(--border);
  border-radius: var(--radius-sm);
  color: var(--text-primary);
  cursor: pointer;
  font-size: 0.85rem;
  transition: border-color var(--transition-fast);

  &:hover:not(:disabled) {
    border-color: var(--accent);
    color: var(--accent);
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }

  svg.spinning {
    animation: spin 1s linear infinite;
  }
}

@keyframes spin {
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
}

.refresh-error {
  margin: 0;
  font-size: 0.85rem;
  color: var(--danger, #e55);
}

.refresh-success {
  margin: 0;
  font-size: 0.85rem;
  color: var(--success, #7ec8a4);
}

/* Mobile: slide up as a bottom-sheet */
@media (max-width: 640px) {
  .vote-config-backdrop {
    align-items: flex-end;
  }

  .vote-config-panel {
    width: 100vw;
    max-height: 92dvh;
    border-radius: var(--radius-lg) var(--radius-lg) 0 0;
    border-bottom: none;
  }
}
</style>
